import type { Pbx } from '@/stores/cta/api'
import type { Device } from '@/stores/device/api'
import type { OfficeDesk } from '@/stores/officeDesk/store'
import { isMyEasybellCom } from '@/utils/environment'
import { isNullish } from '@/utils/parsing'
import { cloneDeep } from 'lodash'
import type { AudioFile } from '@/stores/audioFile/api'
import type { ResourceData } from '@/stores/resource/store'

export type CtaData = Omit<Pbx, 'catchAllExtension'> & {
  ctaId: number
  current: string
  downgrade: string
  upgrade: string
  catchAllExtension: number | null
  isTapiConnected: boolean
  tapiWhitelistedIps: string[]
  tapiUsername: string
  tapiPassword: string
  adminMail: string
  adminPhone: string
  showVoipToGoOfficedesks?: boolean
  normalisePhonebook?: boolean
  language: string
  serviceCountry?: string
}

// * CTA
export function transformIncomingCta(pbxOrArray: Pbx | Pbx[]) {
  const pbx = Array.isArray(pbxOrArray) ? pbxOrArray.pop()! : pbxOrArray

  const ctaData: CtaData = {
    ...pbx,
    ctaId: pbx.id,
    current: pbx.currentTariff,
    downgrade: pbx.downgradeTariff,
    upgrade: pbx.upgradeTariff,
    catchAllExtension: pbx.catchAllExtension?.id ?? null,
    isTapiConnected: pbx.tapiConnection !== null,
    tapiWhitelistedIps: pbx.tapiConnection?.ips ?? [],
    tapiUsername: pbx.tapiConnection?.username,
    tapiPassword: pbx.tapiConnection?.password,
    adminMail: pbx.adminMail ?? '',
    adminPhone: pbx.adminPhone ?? '',
    showVoipToGoOfficedesks: pbx.showVoiptogoofficedesks,
    tapiConnection: pbx.tapiConnection,
    groupTags: pbx.groupTags,
  }

  return ctaData
}

export function transformOutgoingCta(ctaData: Partial<CtaData>) {
  ctaData.showVoiptogoofficedesks = ctaData.showVoipToGoOfficedesks
  delete ctaData.showVoipToGoOfficedesks

  return ctaData
}

export type Resource = ResourceData & {
  emailErrorMessage?: string | undefined
}

// * Extension
export function transformIncomingExtension(extensionData: any) {
  // * Applied to extensionData-objects when they arrive from  index- or get-requests
  // * Transforms each extensionData-Object to the shape expected by frontend

  // * renamed & transormed
  extensionData.directDial = extensionData.directDialing
    ? extensionData.directDialing.toString()
    : null
  delete extensionData.directDialing
  extensionData.sms_number = extensionData.smsNumber
  delete extensionData.smsNumber
  if (extensionData.forwardingType === 'call_forwarding') {
    extensionData.forwardingType = 'forwarding'
  }

  // * renamed
  extensionData.isSingleNumber = extensionData.isMsn
  delete extensionData.isMsn
  extensionData.quick_dial = extensionData.quickDial
  delete extensionData.quick_dial
  if (extensionData.allow_refer !== undefined) {
    extensionData.allowRefer = extensionData.allow_refer
    delete extensionData.allow_refer
  }

  if (extensionData.forwardings !== undefined) {
    extensionData.forwardings = transformIncomingForwardings(
      extensionData.forwardings
    )
  }

  extensionData.closing = transformIncomingForwarding(extensionData.closing)

  if (extensionData.extendedForwardingProfiles) {
    extensionData.extendedForwardingProfiles =
      extensionData.extendedForwardingProfiles.reduce((acc: any, curr: any) => {
        const newForwardings = curr.forwardings.map((forwarding: any) => {
          delete forwarding.createdAt
          delete forwarding.updatedAt
          return forwarding
        })
        const profile = { ...curr, forwardings: newForwardings }

        acc.push(profile)

        return acc
      }, [])
  }
  // extensionData.trunkId = extensionData.trunkId.toString()

  // // * transformed & renamed
  // extensionData.showExtensionNameOnDisplay = extensionData.setNameAsPrefix
  // delete extensionData.setNameAsPrefix

  // * Conference fields
  if (extensionData.conference) {
    extensionData.conferencePin = extensionData.conference.config
  } else {
    extensionData.conferencePin = ''
  }
  delete extensionData.conference

  // * deleted
  delete extensionData.enableExtensionQuickdial
  delete extensionData.pbxId
  delete extensionData.zohoUser
  // delete extensionData.extendedProfiles
  // delete extensionData.forwarding
  delete extensionData.pbx

  // Populate first and last names for User, and vice versa for others
  extensionData = populateFirstLastAndName(extensionData)

  extensionData.userExtensions = extensionData.userExtensions?.map(
    (extension: any) => extension.id
  )

  return extensionData
}

export const transformOutgoingExtension = (extensionData: any) => {
  // * Applied to extensionData-objects before they sent via post- or put-requests
  // * Transforms each extensionData-Object to the shape expected by backend

  // * renamed
  if (extensionData.directDial !== undefined) {
    extensionData.directDialing = extensionData.directDial
    delete extensionData.directDial
  }
  if (extensionData.forwardingType === 'forwarding') {
    extensionData.forwardingType = 'call_forwarding'
  }
  if (extensionData.isSingleNumber !== undefined) {
    extensionData.isMsn = extensionData.isSingleNumber
    delete extensionData.isSingleNumber
  }
  if (extensionData.quickDial !== undefined) {
    extensionData.quick_dial = extensionData.quickDial
    delete extensionData.quickDial
  }

  // * transformed
  // if (extensionData.hasOwnProperty("conflict")) {
  //   extensionData.conflict = extensionData.conflict ? 1 : 0
  // }
  // if (extensionData.hasOwnProperty("showExtensionNameOnDisplay")) {
  //   extensionData.setNameAsPrefix = extensionData.showExtensionNameOnDisplay ? 1 : 0
  //   delete extensionData.showExtensionNameOnDisplay
  // }

  // * Conference fields
  if (extensionData.conferencePin !== undefined) {
    extensionData.conference = { config: extensionData.conferencePin }
    delete extensionData.conferencePin
  }

  return extensionData
}

export const reduceIncomingExtension = (extensionData: any) => {
  // * Only applied on extensionData-objects received from an index-request
  // * deletes unnecessary properties from the extensionData-Objects

  // * deleted
  delete extensionData.enableExtensionQuickdial

  return extensionData
}

export type TransformedDevice = Device & {
  pbxId: never
  createdAt: never
  updatedAt: never
  blockOutgoing: never
  clip: never
  allowOutgoingCalls: boolean
  appFax: boolean
  appSms: boolean
  appContacts: boolean
  appCallLog: boolean
  appExtensions: number[]
  appOfficeDeskId?: number
}

// * Device
export const transformIncomingDevice = (originalDevice: Device) => {
  const transformedDevice = cloneDeep(originalDevice) as TransformedDevice

  // Remove fields
  delete transformedDevice.pbxId
  delete transformedDevice.createdAt
  delete transformedDevice.updatedAt

  // Rename & Transform
  transformedDevice.allowOutgoingCalls = !transformedDevice.blockOutgoing
  delete transformedDevice.blockOutgoing

  // backend returns these as strings after create/update
  if (!isNullish(transformedDevice.cluster)) {
    transformedDevice.cluster = Number(transformedDevice.cluster)
  }
  if (!isNullish(transformedDevice.syncLevel)) {
    transformedDevice.syncLevel = Number(transformedDevice.syncLevel)
  }

  if (
    transformedDevice.outgoingTrunk !== null &&
    typeof transformedDevice.outgoingTrunk === 'object'
  ) {
    transformedDevice.outgoingTrunk = transformedDevice.outgoingTrunk.id
  }

  delete transformedDevice.clip

  transformedDevice.appFax = false
  transformedDevice.appSms = false
  transformedDevice.appContacts = false
  transformedDevice.appCallLog = false
  transformedDevice.appExtensions = []
  transformedDevice.clips = originalDevice.clips?.map((clip) => ({
    ...clip,
    clip_ndc: clip.clip_ndc ?? '',
    clip_sn: clip.clip_sn ?? '',
  }))

  if (transformedDevice.officeDesk) {
    transformedDevice.appOfficeDeskId = transformedDevice.officeDesk.id
    transformedDevice.appFax = transformedDevice.officeDesk.faxPermission
    transformedDevice.appSms = transformedDevice.officeDesk.smsPermission
    transformedDevice.appContacts =
      transformedDevice.officeDesk.contactsPermission
    transformedDevice.appCallLog =
      transformedDevice.officeDesk.callLogPermission
    transformedDevice.appExtensions = [
      ...transformedDevice.officeDesk.extensions,
    ]
  }
  delete transformedDevice.officeDesk

  return transformedDevice
}

export const reduceIncomingDevice = (deviceData: any) => {
  return deviceData
}

export const transformOutgoingDevice = (deviceData: any) => {
  // Rename & Transform
  if (deviceData.allowOutgoingCalls !== undefined) {
    deviceData.blockOutgoing = !deviceData.allowOutgoingCalls
    delete deviceData.allowOutgoingCalls
  }

  if (deviceData.provisioningTemplate) {
    deviceData.provisioningTemplate = Number(deviceData.provisioningTemplate)
  }

  // * If Device is type=app, the App-Device-OfficeDesk-Data is split of into separate object.
  // * This is needed, because a separate, second request needs to be sent in device.js
  let officeDeskData = {
    ...(deviceData.appFax !== undefined && {
      faxPermission: deviceData.appFax,
    }),
    ...(deviceData.appSms !== undefined && {
      smsPermission: deviceData.appSms,
    }),
    ...(deviceData.appContacts !== undefined && {
      contactsPermission: deviceData.appContacts,
    }),
    ...(deviceData.appCallLog !== undefined && {
      callLogPermission: deviceData.appCallLog,
    }),
    ...(deviceData.appExtensions !== undefined && {
      extensions: deviceData.appExtensions,
    }),
    ...(deviceData.type === 'app' && deviceData.email !== undefined && {
      email: deviceData.email,
    }),
  }
  if (Object.keys(officeDeskData).length === 0) {
    // @ts-ignore
    officeDeskData = null
  }
  delete deviceData.appFax
  delete deviceData.appSms
  delete deviceData.appContacts
  delete deviceData.appCallLog
  delete deviceData.appExtensions
  return { deviceData, officeDeskData }
}

// * Forwardings
export const transformIncomingForwardings = (forwardingData: any) => {
  return {
    general: transformIncomingForwarding(
      forwardingData.find((forwarding: any) => forwarding.trigger === 'online')
    ),
    busy: transformIncomingForwarding(
      forwardingData.find(
        (forwarding: any) => forwarding.trigger === 'occupied'
      )
    ),
    offline: transformIncomingForwarding(
      forwardingData.find((forwarding: any) => forwarding.trigger === 'offline')
    ),
  }
}

export const transformIncomingForwarding = (forwardingData: any) => {
  if (forwardingData) {
    if (forwardingData.notificationType === 'caller_left_message') {
      forwardingData.notificationType = 'leftMessage'
    } else if (forwardingData.notificationType === 'caller_heard_message') {
      forwardingData.notificationType = 'heardMessage'
    }

    // TODO(Simple forwarding): we want to have a descriptive string value for notificationType as in closing
    if (forwardingData.notificationType === '0') {
      forwardingData.notificationType = 'none'
    } else if (forwardingData.notificationType === '1') {
      forwardingData.notificationType = 'leftMessage'
    } else if (forwardingData.notificationType === '2') {
      forwardingData.notificationType = 'heardMessage'
    }

    const containOnlyDigits = /^[+]{0,1}\d+$/.test(forwardingData.phoneNumber)
    if (!containOnlyDigits) {
      forwardingData.phoneNumber = ''
    }

    return forwardingData
  }
  return null
}

// * Prompt
export const transformIncomingPrompt = (prompt: AudioFile) => {
  const translations: Record<number, string> = {
    1: 'global.common.standardMessage',
    2: 'global.common.standardPrompt',
    3: 'global.common.standardMusicOnHold',
    4: 'global.common.standardWaitingMusic',
  }

  if (translations[prompt.id]) {
    prompt.name = translations[prompt.id]
  }

  return prompt
}

export const reduceIncomingPrompt = (prompt: any) => {
  delete prompt.file
  delete prompt.createdAt
  delete prompt.updatedAt
  delete prompt.pbxId
  return prompt
}

export const transformOutgoingPrompt = (prompt: any) => {
  return prompt
}

// * Forwarding
// TODO: refactor
export const transformOutgoingForwarding = (forwardingData: any) => {
  if (forwardingData.notificationType === 'leftMessage') {
    forwardingData.notificationType = 'caller_left_message'
  } else if (forwardingData.notificationType === 'heardMessage') {
    forwardingData.notificationType = 'caller_heard_message'
  } else if (forwardingData.notificationType === null) {
    forwardingData.notificationType = 'none'
  }

  // Set channel to empty if type is empty
  if (forwardingData.notificationType === 'none') {
    forwardingData.notificationChannel = ''
  }

  forwardingData.notificationChannel =
    forwardingData.notificationChannel === 'smsAndEmail'
      ? 'sms_and_email'
      : forwardingData.notificationChannel

  forwardingData.prompt = Number(forwardingData.promptId)
  delete forwardingData.promptId
  delete forwardingData.createdAt
  delete forwardingData.updatedAt

  if (forwardingData.type === 'internal') {
    forwardingData.forwardTo = forwardingData.forwardToExtensionId
    delete forwardingData.notificationType
    delete forwardingData.notificationChannel
    delete forwardingData.phoneNumber
  } else if (forwardingData.type === 'prompt') {
    delete forwardingData.phoneNumber
    delete forwardingData.forwardingExtensionId
  } else if (forwardingData.type === 'external') {
    delete forwardingData.notificationType
    delete forwardingData.notificationChannel
  }
  return forwardingData
}

export const transformOutgoingForwardings = ({
  general,
  busy,
  offline,
}: any) => {
  const transformedForwardings = [
    {
      trigger: 'online',
      ...transformOutgoingForwarding(general),
    },
    {
      trigger: 'occupied',
      ...transformOutgoingForwarding(busy),
    },
    {
      trigger: 'offline',
      ...transformOutgoingForwarding(offline),
    },
  ]

  return transformedForwardings
}

export const transformOutgoingStep = (stepData: any) => {
  return stepData
}

export const transformIncomingOfficeDesk = (
  officeDeskData: OfficeDesk & { active?: number | boolean }
) => {
  if (officeDeskData.active === 0) {
    officeDeskData.active = false
  } else if (officeDeskData.active === 1) {
    officeDeskData.active = true
  }

  return officeDeskData
}

export const getCountryCodeFromTrunks = (trunks: Pbx['trunks']) => {
  if (!Array.isArray(trunks) || trunks.length < 1) {
    return 'de'
  }

  const prefix = trunks.at(0)?.e164InternationalPrefix || '0049'

  const countryCodes: Record<string, string> = {
    '0049': 'de',
    '0043': 'at',
    '0033': 'fr',
    '0031': 'nl',
  }

  return countryCodes[prefix]
}

export type Trunk = NonNullable<
  ReturnType<typeof transformIncomingTrunks>
>[number]

export const transformIncomingTrunks = (trunks: Pbx['trunks']) => {
  if (isMyEasybellCom()) {
    const trunks_ =
      trunks
        ?.filter(
          (
            trunk
          ): trunk is Pbx['trunks'][number] & {
            phoneNumber: NonNullable<Pbx['trunks'][number]['phoneNumber']>
          } => {
            return !!trunk.phoneNumber
          }
        )
        .map((trunk) => {
          let prefix = '0'
          let blockRange = null
          let showBlockRangeNumeric = true
          trunk.showAsTrunkAndSingleNumber = false
          switch (trunk.phoneNumber.slice(0, 2)) {
            case '49':
              prefix = 'DE: +'
              break
            case '43':
              prefix = 'AT: +'
              showBlockRangeNumeric = false
              trunk.showAsTrunkAndSingleNumber = true
              break
            case '33':
              prefix = 'FR: +'
              break
            case '31':
              prefix = 'NL: +'
              break
          }

          if (showBlockRangeNumeric) {
            blockRange = trunk.isBlockNumber
              ? `${trunk.blockRangeStart}-${trunk.blockRangeEnd}`
              : null
          } else {
            blockRange = 'X'.repeat(String(trunk.blockSize - 1).length)
          }

          let numberLabel = prefix.concat(trunk.phoneNumber)
          if (blockRange) {
            numberLabel += ` (${blockRange})`
          }
          // }
          return {
            singleNumber: undefined,
            ...trunk,
            blockRange,
            numberLabel,
          }
        }) ?? []
    return trunks_
  } else {
    const trunks_ =
      trunks?.map((trunk) => {
        const showAsTrunkAndSingleNumber = true
        const blockRange = trunk.isBlockNumber
          ? `${trunk.blockRangeStart}-${trunk.blockRangeEnd}`
          : null
        let numberLabel = '0' + trunk.label.substring(4)
        const singleNumber = numberLabel
        if (blockRange) {
          numberLabel += ` (${blockRange})`
        }
        return {
          ...trunk,
          blockRange,
          numberLabel,
          singleNumber,
          showAsTrunkAndSingleNumber,
        }
      }) ?? []

    return trunks_
  }
}

export function populateFirstLastAndName(extensionData: any) {
  if (extensionData.type === 'user') {
    const name = getNameFromFirstAndLastNames(
      extensionData.firstname,
      extensionData.lastname
    )

    return {
      ...extensionData,
      name,
    }
  }
  const { firstname, lastname } = getFirstAndLastNamesFromName(
    extensionData.name
  )

  return {
    ...extensionData,
    firstname,
    lastname,
  }
}

export function getFirstAndLastNamesFromName(name?: string) {
  const spaceRegex = /^(.*)\s(\S*)$/

  if (!name) {
    return {
      firstname: undefined,
      lastname: undefined,
    }
  }

  if (spaceRegex.test(name.trim())) {
    const matchResult = name.trim().match(spaceRegex)

    if (matchResult) {
      const [, firstname, lastname] = matchResult

      return {
        firstname,
        lastname,
      }
    }
  }

  return {
    firstname: name,
    lastname: '',
  }
}

export function getNameFromFirstAndLastNames(
  firstname?: string,
  lastname?: string
) {
  return firstname + ' ' + lastname
}
